/**
 * API client for the reporting surface (llmr-report-api).
 *
 * Separate from `client.ts` for exactly one reason: the base path. Reports are
 * served at `/report/*` while the member API is at `/api/*`. Both are
 * SAME-ORIGIN on this hostname — the proxy routes `/report/` to report-api on
 * the member host and the admin host alike (ADR-0013 §1) — so the session
 * cookie reaches both without a second credential and without CORS.
 *
 * There is no bearer token here and there must never be one. This service
 * validates the same session cookie the portal already holds, which is what
 * makes signing out end report access too.
 *
 * The error model, the network-failure type and the fetch options are shared
 * with `client.ts` rather than reimplemented: two clients that disagree about
 * what an error looks like is how a portal grows two error banners.
 */

import { ApiError, NetworkError, type Problem } from './client';
import type { components } from './reportSchema.gen';

const BASE = '/report';

export type ReportType = components['schemas']['ReportType'];
export type ReportFormat = components['schemas']['ReportFormat'];
export type ReportLocale = components['schemas']['ReportLocale'];
export type ReportDescriptor = components['schemas']['ReportDescriptor'];
export type ReportColumn = components['schemas']['ReportColumn'];
export type ReportPage = components['schemas']['ReportPage'];
export type ReportSummary = components['schemas']['ReportSummary'];
export type ExportResult = components['schemas']['ExportResult'];

/** One row of a report. Keys are the column `key`s; values are whatever the column `type` says. */
export type ReportRow = Record<string, unknown>;

/**
 * The two canonical page shapes — there is no third, and reports do not get
 * one of their own (ADR-0009 §1).
 *
 * Aggregates page by OFFSET and carry a total, because "24 models, showing
 * 1–20" is the useful answer for a bounded set. `ledger_statement` pages by
 * KEYSET, because it is append-only money data read during a billing dispute
 * and offset drift would displace a row across a page boundary while the
 * reader is looking at it.
 *
 * Which one a report uses is on its descriptor as `paging`, so the portal
 * renders the matching control instead of guessing from the response.
 */
export type ReportPageInfo =
  | import('./schema.gen').components['schemas']['PageInfo']
  | import('./schema.gen').components['schemas']['OffsetPageInfo'];

/** Narrowing helper: offset pages carry a total and a page number, keyset pages do not. */
export function isOffsetPage(p: ReportPageInfo): p is import('./schema.gen').components['schemas']['OffsetPageInfo'] {
  return typeof (p as { page?: unknown }).page === 'number';
}

async function request<T>(path: string, options: { method?: 'GET' | 'POST'; body?: unknown; signal?: AbortSignal } = {}): Promise<T> {
  const { method = 'GET', body, signal } = options;

  const headers: Record<string, string> = {};
  if (body !== undefined) headers['Content-Type'] = 'application/json';

  let response: Response;
  try {
    response = await fetch(`${BASE}${path}`, {
      method,
      headers,
      // The session is an HttpOnly cookie; nothing is exposed to JavaScript.
      credentials: 'same-origin',
      body: body === undefined ? undefined : JSON.stringify(body),
      signal,
    });
  } catch (cause) {
    if (cause instanceof DOMException && cause.name === 'AbortError') throw cause;
    throw new NetworkError(cause);
  }

  const isProblem = response.headers.get('content-type')?.includes('application/problem+json');

  if (!response.ok) {
    if (isProblem) {
      throw new ApiError((await response.json()) as Problem, response.status);
    }
    // A non-problem error body means something upstream answered — a proxy 502,
    // an HTML error page. Do not pretend it is ours.
    throw new ApiError(
      {
        type: '/problems/unexpected',
        title: 'Unexpected response',
        status: response.status,
        detail: `the server returned ${response.status} without a problem document`,
      },
      response.status,
    );
  }

  return (await response.json()) as T;
}

function qs(params: Record<string, unknown>): string {
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === '') continue;
    q.set(k, String(v));
  }
  const s = q.toString();
  return s ? `?${s}` : '';
}

export interface ReportQuery {
  dateFrom?: string;
  dateTo?: string;
  /** Keyset reports only. */
  cursor?: string;
  limit?: number;
  /** Offset reports only. 1-based, as the contract defines it. */
  page?: number;
  pageSize?: number;
  locale?: ReportLocale;
}

/**
 * NOTE ON `org_id`: it is deliberately absent from every signature here.
 *
 * A member session is scoped by the SERVER, from the session, and sending
 * `org_id` on that path is a 403 rather than a silently ignored parameter
 * (ADR-0013 §1). The admin portal's own client is where that parameter belongs.
 * Adding it here would not widen anything — the server refuses — but it would
 * suggest to the next reader that it might.
 */
export const reportApi = {
  /**
   * The report types this caller may run.
   *
   * Driven by the server from the principal, so the menu is not a client-side
   * constant that can drift from what the server will actually serve — and a
   * staff-only report never reaches a customer's browser at all.
   */
  catalog: (locale?: ReportLocale, signal?: AbortSignal) =>
    request<{ data: ReportDescriptor[] }>(`/reports${qs({ locale })}`, { signal }),

  run: (type: ReportType, q: ReportQuery = {}, signal?: AbortSignal) =>
    request<ReportPage & { page: ReportPageInfo }>(
      `/reports/${type}${qs({
        date_from: q.dateFrom,
        date_to: q.dateTo,
        cursor: q.cursor,
        limit: q.limit,
        page: q.page,
        page_size: q.pageSize,
        locale: q.locale,
      })}`,
      { signal },
    ),

  /**
   * Generate a file and return a short-lived presigned link.
   *
   * Not idempotent, on purpose: the filters may name "today", and a cached
   * yesterday would be a wrong answer delivered quickly. The returned
   * `download_url` points at object storage, not at this API, and fetching it
   * does NOT consume it — the same URL may be retried until `expires_at`.
   */
  exportFile: (type: ReportType, format: ReportFormat, q: ReportQuery = {}, signal?: AbortSignal) =>
    request<ExportResult>(`/reports/${type}/export`, {
      method: 'POST',
      body: {
        format,
        locale: q.locale,
        date_from: q.dateFrom,
        date_to: q.dateTo,
      },
      signal,
    }),

  /**
   * Presigned link for one DRAFT e-Tax invoice mock (LLMR-90). Not a real
   * tax document — see TaxInvoiceInput / TopUp.invoice_id in member-api.yaml.
   */
  downloadInvoice: (invoiceId: string, signal?: AbortSignal) =>
    request<{ download_url: string }>(`/invoices/${encodeURIComponent(invoiceId)}/download`, { signal }),
};

/** The problem type report-api returns when the PDF renderer is unreachable. */
export const RENDER_UNAVAILABLE = 'https://llm-router.i-c-develop.com/problems/dependency-unavailable';
